import React, { FC, useCallback, useMemo, useState } from 'react'

import { TrashIcon } from '@heroicons/react/24/outline'
import { groupBy, sortBy, uniqBy } from 'lodash'
import _ from 'lodash'

import css from './styles.module.css'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { DELETED_FOLDER, ROOT_FOLDERS_CHILDREN } from '@/features/FileManager/FileManager'
import { TreeViewerFolder } from '@/features/FileManager/TreeViewerFolder'
import {
  FileManagerFile,
  ROOT_FOLDER,
  ROOT_FOLDERS,
  setSelectedFolder,
  setSelectedFolderParent,
} from '@/features/FileManager/fileManagerSlice'
import { Application } from '@/graphql/generated'
import { useGetApplicationCalcsQuery } from '@/graphql/services/applicationCalcs'
import useShouldHideDTI from '@/utils/useShouldHideDTI'

interface TreeViewerProps {
  files?: FileManagerFile[]
  application: Application
}

export const TreeViewer: FC<TreeViewerProps> = ({ files, application }) => {
  const dispatch = useAppDispatch()

  const selectedFolder = useAppSelector((state) => state.fileManager.selectedFolder)

  const [openedFolders, setOpenedFolders] = useState<string[]>(ROOT_FOLDERS)
  const { data: applicationCalcs } = useGetApplicationCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application?.externalId,
    }
  )

  // TODO: Only show Servicing folder if in servicing stage. Currently not possible due to product requirement fix.
  // const { data: applicationCalcs, isLoading: isLoadingApplicationCalcs } =
  // useGetApplicationCalcsQuery(application.uuid, {
  //   skip: !application,
  // })
  // const ROOT_FOLDERS = useMemo(() => {
  //   if (isLoadingApplicationCalcs) return ROOT_FOLDERS
  //   return applicationCalcs.is_servicing
  //     ? ROOT_FOLDERS
  //     : ROOT_FOLDERS.filter((rootFolder) => rootFolder != SERVICING_FOLDER)
  // }, [isLoadingApplicationCalcs])

  const splitFolderName = (folderName: string) => {
    const i = folderName.indexOf('/')
    return [folderName.slice(0, i), folderName.slice(i + 1)]
  }

  const mapFolderStructure = (parentFolderName, folders, sortAlphabetically: boolean) => {
    let output = folders.map((folder) => ({
      folderName: folder,
      opened: false,
    }))

    if (files?.length) {
      const fileFolders = Object.keys(groupBy(files, 'tag'))
        .filter((folder) => {
          if (folder === 'null' || folder === DELETED_FOLDER || ROOT_FOLDERS.includes(folder))
            return false

          const splitedFolder = folder.split('/')
          const [rootFolder] = splitedFolder

          if (splitedFolder.length > 1) {
            return rootFolder === parentFolderName
          }

          return ROOT_FOLDER === parentFolderName
        })
        .map((folder) => {
          const splitedFolder = splitFolderName(folder)

          if (splitedFolder.length === 1) {
            return {
              folderName: folder,
              opened: false,
            }
          }

          const [, childFolder] = splitedFolder

          return {
            folderName: childFolder,
            opened: false,
          }
        })

      output = uniqBy(output.concat(fileFolders), 'folderName')
    }

    return sortAlphabetically ? sortBy(output, (o) => o.folderName) : output
  }

  const isFolderEmpty = (folderName: string) => {
    const folderFiles = files.filter((file) => file.tag === folderName)
    return folderFiles.length == 0
  }

  const shouldHideDTI = useShouldHideDTI()

  const calculateDynamicFolders = (folderTree, application) => {
    /* Hides dynamic folders if conditions are met */
    const foldersToHide = []

    const foldersConditionsToHide = {
      'NOO Docs': !application?.title_report?.noo_name_of_spouse,
      'HOA Docs': !(
        application?.property?.monthly_cost_hoa && application?.property?.monthly_cost_hoa > 0
      ),
      'Deferment-Forbearance Docs': !application?.atypicality_check?.last_forbearance,

      'DTI Docs':
        applicationCalcs?.application?.applicationCalculations?.calcApplicationFico >= 550 ||
        shouldHideDTI,
      'Rental Docs': application?.property?.property_usage != 'investment',
      'Solar Docs': !application?.property?.roof_solar_panel,
      'POA Docs': !application?.poa_flag,
    }

    for (const [folder, condition] of Object.entries(foldersConditionsToHide)) {
      if (isFolderEmpty(folder) && condition) foldersToHide.push(folder)
    }

    _.pull(folderTree[ROOT_FOLDER]?.children, ...foldersToHide)

    return folderTree
  }

  const rootFoldersChildrenMapped = useMemo(() => {
    const output = {}

    const foldersTree = calculateDynamicFolders(ROOT_FOLDERS_CHILDREN, application)

    ROOT_FOLDERS.forEach((rootFolder) => {
      const rootFolderInfo = foldersTree[rootFolder]
      const rootFolderChildren = rootFolderInfo.children
      const sortChildrenAlphabetically = rootFolderInfo.sortAlphabetically

      output[rootFolder] = mapFolderStructure(
        rootFolder,
        rootFolderChildren,
        sortChildrenAlphabetically
      )
    })

    return output
  }, [files, openedFolders])

  const toggleOpenedFolders = useCallback(
    (folder: string) => {
      let updatedFolders

      if (openedFolders.indexOf(folder) === -1) {
        updatedFolders = [...openedFolders, folder]
      } else {
        updatedFolders = openedFolders.filter((f) => f !== folder)
      }

      setOpenedFolders(updatedFolders)
    },
    [openedFolders]
  )

  const handleFolderSelected = useCallback(
    (folder: string, parentFolder: string) => {
      dispatch(setSelectedFolder(folder))
      dispatch(setSelectedFolderParent(parentFolder))
    },
    [dispatch]
  )

  const folderHitMap = useMemo(
    () =>
      files.reduce((hitMap, file) => {
        const folderName = splitFolderName(file.tag).pop()
        hitMap[folderName] = (hitMap[folderName] || 0) + 1
        return hitMap
      }, {}),
    [files]
  )

  const isDateWithInLastDay = (createdAt) => {
    if (!createdAt) return false

    const creationDate = new Date(createdAt)
    const DAY_IN_MILLISECONDS = 60 * 60 * 24 * 1000
    // Check if creationDate is within the last day
    return new Date().getTime() - creationDate.getTime() < DAY_IN_MILLISECONDS
  }

  const hasNewFilesMap = useMemo(
    () =>
      files.reduce((hitMap, file) => {
        const folderName = splitFolderName(file.tag).pop()
        const isNewFile = isDateWithInLastDay(file.created_at)
        hitMap[folderName] = isNewFile || hitMap[folderName]
        return hitMap
      }, {}),
    [files]
  )

  const isFolderSelected = (selectedFolder: string, folderName: string) => {
    return selectedFolder === folderName
  }

  return (
    <div className={`p-2 ${css.treeViewer} border-r w-[280px]`}>
      {ROOT_FOLDERS.map((rootFolder) => {
        const rootFolderChildren = rootFoldersChildrenMapped[rootFolder] || []

        return (
          <TreeViewerFolder
            key={rootFolder}
            folder={rootFolder}
            fileCount={folderHitMap[rootFolder]}
            opened={openedFolders.indexOf(rootFolder) !== -1}
            onFolderClicked={toggleOpenedFolders}
            onFolderSelected={handleFolderSelected}
            selected={isFolderSelected(selectedFolder, rootFolder)}
          >
            {rootFolderChildren?.length > 0 &&
              rootFolderChildren.map(({ folderName, opened }) => (
                <TreeViewerFolder
                  key={folderName}
                  folder={folderName}
                  fileCount={folderHitMap[folderName]}
                  parentFolder={rootFolder}
                  opened={opened}
                  onFolderClicked={toggleOpenedFolders}
                  onFolderSelected={handleFolderSelected}
                  selected={isFolderSelected(selectedFolder, folderName)}
                  level={1}
                  hasNewFiles={hasNewFilesMap[folderName]}
                />
              ))}
          </TreeViewerFolder>
        )
      })}
      <TreeViewerFolder
        folder={DELETED_FOLDER}
        fileCount={folderHitMap[DELETED_FOLDER]}
        opened={openedFolders.indexOf(ROOT_FOLDER) !== -1}
        onFolderClicked={toggleOpenedFolders}
        onFolderSelected={handleFolderSelected}
        selected={isFolderSelected(selectedFolder, DELETED_FOLDER)}
        customIcon={
          <TrashIcon
            width={20}
            stroke={'grey'}
          />
        }
      />
    </div>
  )
}
